/**
 * TeamPeriodControls — the reporting-period chips and the custom date pair, URL-driven.
 *
 * The RenewalsPage idiom, re-used structurally (a closed list of chips, `custom` as a
 * MODE reached by touching a date rather than a sixth chip, `aria-pressed` on the active
 * chip) over the ONE web period vocabulary in `./periods` (`PERIOD_CHIPS`, shared with the
 * country dashboard and the reports, so every period-taking screen offers the same five
 * words). The chips say what was ASKED; the screen's resolved-window line says what the
 * SERVER used, per country — the two can differ across a timezone, which is why both
 * exist.
 *
 * Dates are `type="date"` with the field's `[color-scheme]` following the theme so the
 * native picker is not a white box on the dark surface (the field registers' `bits.tsx`
 * recipe).
 */

import { useId } from 'react';
import { cn } from '../../lib/cn';
import { PERIOD_CHIPS } from './periods';
import type { DashboardPeriod } from './periods';

const DATE_BOX =
  'h-8 rounded-sm border border-line bg-surface px-2 text-body13 text-fg [color-scheme:light] dark:[color-scheme:dark]';

export function TeamPeriodControls({
  period,
  from,
  to,
  onPeriod,
  onCustom,
}: {
  /** Whatever the URL says — unvalidated, the server answers a stranger with a 400. */
  period: string;
  from: string;
  to: string;
  onPeriod: (value: DashboardPeriod) => void;
  onCustom: (which: 'from' | 'to', value: string) => void;
}): React.JSX.Element {
  const fromId = useId();
  const toId = useId();
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
      <div role="group" aria-label="Reporting period" className="flex flex-wrap gap-2">
        {PERIOD_CHIPS.map((chip) => (
          <button
            key={chip.value}
            type="button"
            aria-pressed={period === chip.value}
            onClick={() => onPeriod(chip.value)}
            className={cn(
              'h-8 rounded-sm border border-line bg-surface px-2.5 text-body13 text-fg-secondary hover:text-fg',
              period === chip.value && 'border-line-strong font-medium text-fg',
            )}
          >
            {chip.label}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <label className="text-body13 text-fg" htmlFor={fromId}>
          From
        </label>
        <input
          id={fromId}
          type="date"
          value={from}
          onChange={(event) => onCustom('from', event.target.value)}
          className={DATE_BOX}
        />
        <label className="text-body13 text-fg" htmlFor={toId}>
          To
        </label>
        <input
          id={toId}
          type="date"
          value={to}
          onChange={(event) => onCustom('to', event.target.value)}
          className={DATE_BOX}
        />
      </div>
    </div>
  );
}
